import { forwardRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  reenterWord,
  type BreakdownItem,
  type GenerationDetail,
  type GenerationPrefill,
  type ItemStatusView,
} from '@entities/Generation';
import { markLabels } from '@features/CardsWorkspace';
import { trackEvent } from '@shared/analytics';
import { cn } from '@shared/lib';
import { StatusGlyph, TokenIcon } from '@shared/ui';
import { canAddManually, retryFailureLine } from '../lib/retryError';
import { useRetryItem } from '../model/useRetryItem';
import { Spinner, TONE_CLASS } from './SenseLine';

export interface ItemActionsContext {
  surface: 'desktop' | 'phone';
  /** Строка создания заблокирована (почта не подтверждена): «Ввести снова» и «Добавить вручную» нет. */
  composerLocked: boolean;
  /** Жетоны закончились: «Повторить» неактивна, подпись «Жетоны закончились». */
  quotaOut: boolean;
  onReenter: (prefill: GenerationPrefill) => void;
}

/** «Ввести снова» (ТЗ § 22.6): слово поиска, модуль записи и недобравшиеся значения элемента. */
export function reenterPrefill(detail: GenerationDetail, entry: BreakdownItem): GenerationPrefill {
  return {
    word: reenterWord(detail, entry.item),
    moduleId: detail.module?.id ?? null,
    picks: entry.failedPicks.map((pick) => ({ front: entry.item.frontName, pos: pick.pos, translation: pick.translation })),
  };
}

/** Кнопка «Ввести снова» — и в строке элемента, и в первой недобравшейся строке готового элемента. */
export function ReenterButton({ detail, entry, ctx }: { detail: GenerationDetail; entry: BreakdownItem; ctx: ItemActionsContext }) {
  const { t } = useTranslation();
  if (ctx.composerLocked) return null;
  return (
    <button
      type="button"
      className="gl-act"
      onClick={() => {
        trackEvent('generation_action', { surface: ctx.surface, action: 'reenter', kind: detail.kind });
        ctx.onReenter(reenterPrefill(detail, entry));
      }}
    >
      <span className="gl-act-face">{t('generations.action.reenter')}</span>
    </button>
  );
}

interface ManualButtonProps {
  detail: GenerationDetail;
  entry: BreakdownItem;
  moduleId: string;
  ctx: ItemActionsContext;
}

/**
 * «Добавить вручную» (ТЗ § 22.6): то же слово ручной карточкой, без жетона — у отказа модерации и вместо
 * «Повторить», когда слова нет в словаре (§ 23.5). Слово элемента (у фразы — сама фраза) и модуль записи встают
 * в строку создания в режиме ручной карточки.
 */
const ManualButton = forwardRef<HTMLButtonElement, ManualButtonProps>(function ManualButton({ detail, entry, moduleId, ctx }, ref) {
  const { t } = useTranslation();
  return (
    <button
      ref={ref}
      type="button"
      className="gl-act"
      onClick={() => {
        trackEvent('generation_action', { surface: ctx.surface, action: 'manual', kind: detail.kind });
        ctx.onReenter({ word: entry.item.frontName, moduleId, mode: 'manual', sourceId: detail.id });
      }}
    >
      <span className="gl-act-face">{t('generations.action.manual')}</span>
    </button>
  );
});

/**
 * Строка элемента со статусом (ТЗ § 22.5): «что» (у фразы — фраза и переводы одной строкой, у слова —
 * переводы) | статус и под ним причина | действие справа. Стопкой — «что» (у фразы — фраза и сразу под ней
 * перевод), затем статус и причина, действие — своей строкой вправо: перевод не отрывается от фразы строкой
 * статуса (приёмка A2-07). Порядок в DOM — порядок чтения; колонки раскладывает сетка по областям. Ошибка
 * «Повторить» — строкой под элементом.
 */
export function ItemLine({
  detail,
  entry,
  view,
  headWord,
  ctx,
}: {
  detail: GenerationDetail;
  entry: BreakdownItem;
  view: ItemStatusView;
  /** Фраза, фразовый глагол или слово подборки — «что» начинается с самой фразы. */
  headWord: boolean;
  ctx: ItemActionsContext;
}) {
  const { t } = useTranslation();
  const { item } = entry;
  const marks = headWord ? markLabels(t, item.meanings[0]?.senses[0]?.marks ?? { registers: [], region: [], grammar: [] }) : [];
  const reason = view.reason ? ('text' in view.reason ? view.reason.text : t(view.reason.key, view.reason.params)) : null;
  const moduleId = detail.module?.id ?? null;
  const retry = view.action === 'retry';
  const suggestion = view.state === 'typo' ? (item.problem?.suggestions.find((s) => s.trim()) ?? null) : null;
  return (
    <div className={cn('gl-r gl-item', view.dim && 'opacity-60')}>
      <div className="gl-i-head">
        {headWord ? (
          <span className="inline-flex flex-wrap items-baseline gap-x-2">
            <span lang="en" className="gl-phrase">
              {item.frontName}
            </span>
            {marks.length > 0 && <span className="gl-marks !ml-0">{marks.join(', ')}</span>}
          </span>
        ) : entry.translationsLine ? (
          <span className="gl-sub">{entry.translationsLine}</span>
        ) : (
          // Переводов нет (идущий элемент без значений, упавшее слово без выбора) — само слово, шрифтом фразы и с
          // lang="en": иначе диктор читал бы английское слово по-русски (§ 22.5, решение по ревью этапа 4).
          <span lang="en" className="gl-phrase">
            {item.frontName}
          </span>
        )}
      </div>
      {headWord && entry.translationsLine && <div className="gl-i-sub gl-sub">{entry.translationsLine}</div>}
      {view.label && (
        <span className={cn('gl-i-status', TONE_CLASS[view.tone])}>
          {view.ring && <StatusGlyph kind="generating" size={12} />}
          {t(view.label.key, view.label.params)}
        </span>
      )}
      {/* Причину у «Повторить» рисует RetryAction: ответ «нет в словаре» её прячет (A2-10). */}
      {reason && !retry && <div className="gl-i-reason gl-sub">{reason}</div>}
      {retry ? (
        <RetryAction detail={detail} entry={entry} ctx={ctx} reason={reason} />
      ) : view.action === 'manual' ? (
        moduleId && canAddManually(ctx, moduleId) ? (
          <div className="gl-i-act">
            <ManualButton detail={detail} entry={entry} moduleId={moduleId} ctx={ctx} />
          </div>
        ) : null
      ) : view.action === 'fix' ? (
        // Опечатка и нераспознанное слово разбираются прямо здесь (страницы «Из словаря» больше нет, решение
        // владельца 2026-10-21): есть подсказка — исправленное слово в строку, нет — ручная карточка.
        moduleId && canAddManually(ctx, moduleId) ? (
          <div className="gl-i-act">
            {suggestion ? (
              <button
                type="button"
                className="gl-act"
                onClick={() => {
                  trackEvent('generation_action', { surface: ctx.surface, action: 'fix', kind: detail.kind });
                  ctx.onReenter({ word: suggestion, moduleId });
                }}
              >
                <span className="gl-act-face">{t('generations.action.enterWord', { word: suggestion })}</span>
              </button>
            ) : (
              <ManualButton detail={detail} entry={entry} moduleId={moduleId} ctx={ctx} />
            )}
          </div>
        ) : null
      ) : view.action === 'reenter' && !ctx.composerLocked ? (
        <div className="gl-i-act">
          <ReenterButton detail={detail} entry={entry} ctx={ctx} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * «Повторить · 1 жетон» со своей строкой ошибки под элементом (ТЗ § 22.6, § 23.5). Слова нет в словаре — на её
 * месте «Добавить вручную», пока запись раскрыта, а прежняя причина элемента («…попробуй ещё раз») скрыта: два
 * противоречащих сообщения подряд (приёмка A2-10). Жетоны закончились — та же кнопка неактивной «Жетоны
 * закончились», а после своего 429 — скрытое объявление вместо второй видимой строки.
 */
function RetryAction({
  detail,
  entry,
  ctx,
  reason,
}: {
  detail: GenerationDetail;
  entry: BreakdownItem;
  ctx: ItemActionsContext;
  /** Причина элемента под статусом. */
  reason: string | null;
}) {
  const { t } = useTranslation();
  const retry = useRetryItem(detail, entry.item, ctx.surface);
  const { failure, clear } = retry;
  const moduleId = detail.module?.id ?? null;
  // Жетонов нет по квоте или по своему 429 — кнопка «Жетоны закончились» сразу, не дожидаясь, пока обнулённый
  // остаток дойдёт из кэша квоты до секции.
  const out = (ctx.quotaOut || !!failure?.noCredits) && !retry.busy;
  const manual = !!failure?.manual;
  const manualShown = manual && moduleId !== null && canAddManually(ctx, moduleId);
  const manualRef = useRef<HTMLButtonElement>(null);
  const lineRef = useRef<HTMLParagraphElement>(null);

  // «Повторить» сменилась на «Добавить вручную» — фокус на неё (без неё — на строку ошибки): кнопка, на которую
  // нажали, исчезла, и фокус иначе упал бы на body.
  useEffect(() => {
    if (manual) (manualRef.current ?? lineRef.current)?.focus({ preventScroll: true });
  }, [manual]);

  // Квота перечиталась, а жетоны есть (возврат, новый день) — объявление о своём 429 больше не верно.
  const wasOut = useRef(ctx.quotaOut);
  useEffect(() => {
    if (wasOut.current && !ctx.quotaOut && failure?.noCredits) clear();
    wasOut.current = ctx.quotaOut;
  }, [ctx.quotaOut, failure, clear]);

  const reasonLine = reason && !manual ? <div className="gl-i-reason gl-sub">{reason}</div> : null;
  const line = retryFailureLine(failure, out);
  return (
    <>
      {reasonLine}
      {manual ? (
        manualShown && moduleId ? (
          <div className="gl-i-act">
            <ManualButton ref={manualRef} detail={detail} entry={entry} moduleId={moduleId} ctx={ctx} />
          </div>
        ) : null
      ) : (
        <div className="gl-i-act">
          {/* Одна кнопка на все состояния. Неактивная — через aria-disabled, а не disabled: иначе фокус с кнопки, на
              которую только что нажали, упал бы на body (§ 22.6). Повтор заблокирован в самом запуске. */}
          <button
            type="button"
            className={cn('gl-act', out && 'gl-act--off')}
            onClick={out ? undefined : () => void retry.run()}
            aria-disabled={out || retry.busy || undefined}
            aria-busy={retry.busy || undefined}
            aria-label={out ? undefined : retry.busy ? t('generations.action.retrying') : t('generations.action.retryPaid')}
          >
            <span className="gl-act-face">
              {out ? (
                t('generations.action.noCredits')
              ) : (
                <>
                  {/* Пока запускаем, обычная подпись держит ширину, поверх — кружок и «Запускаем…». */}
                  <span className={cn('inline-flex items-center', retry.busy && 'invisible')}>
                    <span className="gl-wide">{t('generations.action.retryPaid')}</span>
                    <span className="gl-narrow">
                      {t('generations.action.retryShort')}
                      <TokenIcon size={14} />
                    </span>
                  </span>
                  {retry.busy && (
                    <span className="absolute inset-0 flex items-center justify-center gap-1.5">
                      <Spinner className="h-3.5 w-3.5" />
                      {t('generations.action.retrying')}
                    </span>
                  )}
                </>
              )}
            </span>
          </button>
        </div>
      )}
      {(retry.busy || failure?.noCredits) && (
        // Свой 429 жетонов: видимой строки нет — кнопка сама стала «Жетоны закончились», диктору — отсюда, один раз.
        // Область появляется пустой с нажатием и получает текст с ответом: live-область, вставленная сразу с
        // текстом, озвучивается ненадёжно.
        <p role="status" className="sr-only">
          {failure?.noCredits ? t('generations.action.noCredits') : ''}
        </p>
      )}
      {line && (
        // Ответ на нажатие «Повторить» — объявляется (исключение к § 22.11, «секция не live»).
        <p
          ref={lineRef}
          role="alert"
          tabIndex={manual && !manualShown ? -1 : undefined}
          className="gl-i-err text-[14px] leading-5 text-danger"
        >
          {t(line)}
        </p>
      )}
    </>
  );
}
